import { useEffect, useRef, useState } from "react";
import { CheckCircle2, ChevronDown, ExternalLink, Image as ImageIcon, Loader2, Upload, X } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";
import { dictionary } from "../../../utils/dictionary";
import { getProductDetail } from "../../../services/store/productAxios";
import { uploadImagesToS3 } from "../../../services/s3/s3Service";
import { toBase64 } from "../../../utils/utils";
import { sanitizeUploadFilename } from "../../../utils/sanitizeUploadFilename";
import type { FunnelWithWAFlag, WhatsAppConfirmationImageMode } from "../../../services/whatsapp/whatsappService";

interface Props {
  funnel: FunnelWithWAFlag;
  onRemove: () => void;
  onImageConfigChange: (mode: WhatsAppConfirmationImageMode, imageUrl?: string | null) => Promise<void> | void;
  removing?: boolean;
}

const ConfirmationFunnelCard = ({ funnel, onRemove, onImageConfigChange, removing }: Props) => {
  const { language } = useAuth();
  const [image, setImage] = useState<string>("");
  const [productName, setProductName] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [imagePanelOpen, setImagePanelOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const principal = (funnel.products || []).find(
      (p) => (p.type || "").toUpperCase() === "PRINCIPAL"
    );
    if (!principal) return;

    let cancelled = false;
    getProductDetail(principal.product_id)
      .then((product: { base_image_url?: string; name?: string }) => {
        if (cancelled) return;
        if (product?.base_image_url) setImage(product.base_image_url);
        if (product?.name) setProductName(product.name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [funnel.id]);

  const brandName = funnel.configurations?.name_funnel || funnel.name;
  const imageMode = funnel.whatsapp_confirmation_image_mode || "product";
  const customImage = funnel.whatsapp_confirmation_image_url || "";
  const previewImage = imageMode === "custom" ? customImage : imageMode === "product" ? image : "";

  const handleModeChange = async (mode: WhatsAppConfirmationImageMode) => {
    if (mode === "custom" && !customImage) {
      fileInputRef.current?.click();
      return;
    }
    await onImageConfigChange(mode, mode === "custom" ? customImage : null);
  };

  const handleFileSelect = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const base64File = await toBase64(file);
      const response = await uploadImagesToS3({
        file: base64File,
        folder: "whatsapp-confirmation-images",
        filename: `${Date.now()}-${sanitizeUploadFilename(file.name)}`,
      });
      const imageUrl = response?.s3_url ?? response?.url ?? response;
      if (typeof imageUrl !== "string" || !imageUrl) {
        throw new Error("No image URL received");
      }
      await onImageConfigChange("custom", imageUrl);
    } catch (err) {
      console.error(err);
      toast.error(
        dictionary["ws-config-funnels-image-upload-error"]?.[language] ??
          "No se pudo subir la imagen"
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col justify-between rounded-[20px] shadow-sm border border-gray-200 overflow-hidden bg-white">
      <div className="p-5">
        <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
          <div className="relative w-[84px] h-[84px] rounded-lg shadow-sm border border-gray-200 overflow-hidden flex-shrink-0 bg-gray-50">
            {image ? (
              <img
                src={image}
                alt={productName || brandName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 overflow-hidden">
                <div
                  className="absolute inset-0 animate-pulse"
                  style={{
                    background:
                      "linear-gradient(120deg, #f4f4f5 30%, #e4e4e7 50%, #f4f4f5 70%)",
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col min-w-0">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#DCFCE7] text-[#15803D] mb-1 self-start">
                  <span className="w-1 h-1 rounded-full bg-[#15803D] mr-1" />
                  {dictionary["ws-config-funnels-active"]?.[language] ?? "Activo"}
                </span>
                <h3 className="text-[1rem] leading-[1.4rem] font-semibold text-gray-900 truncate">
                  {brandName}
                </h3>
                {productName && (
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                    {productName}
                  </p>
                )}
              </div>
              {funnel.url && (
                <a
                  href={funnel.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0"
                  title={dictionary["ws-config-funnels-open"]?.[language] ?? "Abrir embudo"}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>

        {(funnel.idSequence || funnel.description) && (
          <div className="mt-4">
            {funnel.idSequence && (
              <div className="text-[.675rem] leading-[1.25rem] text-gray-600 font-semibold mb-1">
                {dictionary["funnel"]?.[language] ?? "Embudo"} #{funnel.idSequence}
              </div>
            )}
            {funnel.description && (
              <p className="text-[13.5px] text-gray-500 font-normal leading-relaxed line-clamp-3">
                {funnel.description}
              </p>
            )}
          </div>
        )}

        <div className="mt-4 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={() => setImagePanelOpen((open) => !open)}
            className="flex w-full items-center justify-between gap-3 rounded-lg text-left text-xs font-semibold text-gray-700 transition-colors hover:text-gray-900"
            aria-expanded={imagePanelOpen}
          >
            <span className="flex min-w-0 items-center gap-2">
              <ImageIcon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">
                {dictionary["ws-config-funnels-image-title"]?.[language] ??
                  "Foto en la confirmación"}
              </span>
            </span>
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                imagePanelOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {imagePanelOpen && (
            <div className="mt-3">
              <div className="mb-3 grid grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1">
                {([
                  ["product", dictionary["ws-config-funnels-image-product"]?.[language] ?? "Base"],
                  ["custom", dictionary["ws-config-funnels-image-custom"]?.[language] ?? "Personal"],
                  ["none", dictionary["ws-config-funnels-image-none"]?.[language] ?? "Sin foto"],
                ] as Array<[WhatsAppConfirmationImageMode, string]>).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => handleModeChange(mode)}
                    className={`flex h-8 items-center justify-center gap-1 rounded-lg text-[11px] font-semibold transition-colors ${
                      imageMode === mode
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {imageMode === mode && <CheckCircle2 className="h-3 w-3" />}
                    {label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <div className="relative h-14 w-20 flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                  {previewImage ? (
                    <img src={previewImage} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[10px] font-medium text-gray-400">
                      {dictionary["ws-config-funnels-image-none"]?.[language] ?? "Sin foto"}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] leading-snug text-gray-500">
                    {imageMode === "product"
                      ? dictionary["ws-config-funnels-image-product-desc"]?.[language] ??
                        "Usa la imagen base del producto principal."
                      : imageMode === "custom"
                        ? dictionary["ws-config-funnels-image-custom-desc"]?.[language] ??
                          "Usa una imagen personalizada para este embudo."
                        : dictionary["ws-config-funnels-image-none-desc"]?.[language] ??
                          "No se enviará imagen en la plantilla."}
                  </p>
                  {imageMode === "custom" && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
                    >
                      {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                      {customImage
                        ? dictionary["ws-config-funnels-image-replace"]?.[language] ?? "Cambiar"
                        : dictionary["ws-config-funnels-image-upload"]?.[language] ?? "Subir"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => handleFileSelect(event.target.files?.[0])}
          />
        </div>
      </div>

      <div className="px-5 pb-5">
        <button
          onClick={onRemove}
          disabled={removing}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gray-100 text-gray-600 text-sm font-medium hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
        >
          {removing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <X className="w-3.5 h-3.5" />
          )}
          {dictionary["ws-config-funnels-remove-from-confirmation"]?.[language] ??
            "Quitar de confirmación"}
        </button>
      </div>
    </div>
  );
};

export default ConfirmationFunnelCard;
